import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/* ── Sidebar card-layout geometry ──
   Shared by the borrower and property sidebars so both drawers open at the
   same size and lay their cards out identically. */

export const MASONRY_COLUMN_GAP = 20;
export const MASONRY_MIN_COLUMN = 320;
/** Two columns become three once each would otherwise exceed this. */
export const MASONRY_WIDE_COLUMN = 650;
/** Used for the first paint, before the container has been measured. */
export const MASONRY_DEFAULT_COLUMNS = 2;

/**
 * Default width for a detail sidebar. Clamped so it can't exceed the viewport
 * on a smaller screen — the user can still drag it down to the 320px minimum,
 * and the layout steps back to two then one column as it narrows.
 */
export const SIDEBAR_DEFAULT_WIDTH = Math.min(
  1450,
  Math.max(680, (typeof window !== "undefined" ? window.innerWidth : 1440) - 80),
);

/**
 * CSS-columns masonry for a stack of PanelCards, matching the Loan Info tab.
 * Cards are uneven in height, and a CSS grid stretches every card in a row to
 * the tallest one, leaving dead space under the short ones. Columns let each
 * card take only the height it needs and the next one flow up beneath it.
 *
 * The column count is measured rather than left to `columns: N <min-width>`,
 * because CSS multicol has only one width floor and this needs two thresholds:
 * a second column once each would clear MASONRY_MIN_COLUMN, a third only once
 * each would otherwise exceed MASONRY_WIDE_COLUMN. One floor can't express
 * both — set it high enough for the 2→3 step and the layout collapses to a
 * single column far too early on the way down.
 *
 * @param {number} cardCount - Cards currently rendered. Caps the column count
 *   so a one-card view renders full width instead of a lone narrow column.
 * @returns {{ref: Function, sx: object}} Spread onto the container Box.
 */
const useCardMasonry = (cardCount = 1) => {
  const [width, setWidth] = useState(0);
  const observerRef = useRef(null);

  // A callback ref rather than an effect — the node only exists while its tab
  // is mounted, so this re-attaches on every tab switch.
  const ref = useCallback((el) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    observerRef.current = ro;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const sx = useMemo(() => {
    const widthAt = (k) => (width - (k - 1) * MASONRY_COLUMN_GAP) / k;

    let n = MASONRY_DEFAULT_COLUMNS;
    if (width) {
      n = 1;
      if (widthAt(2) >= MASONRY_MIN_COLUMN) n = 2;
      if (n === 2 && widthAt(2) >= MASONRY_WIDE_COLUMN) n = 3;
    }
    n = Math.max(1, Math.min(n, Math.max(1, cardCount)));

    return {
      columnCount: n,
      columnGap: `${MASONRY_COLUMN_GAP}px`,
      // `&&` doubles the parent class for specificity. A plain `& > *` ties
      // with the card's own emotion class, and PanelCard sets its own
      // margin-bottom — so without the bump, the cards keep their own spacing
      // and this rule silently loses.
      "&& > *": {
        breakInside: "avoid",
        WebkitColumnBreakInside: "avoid",
        width: "100%",
        mb: 2.5,
      },
    };
  }, [width, cardCount]);

  return { ref, sx };
};

export default useCardMasonry;
